/**
 * Opens Google Picker to select Drive folders/files for source registration.
 */

import React, { useState } from 'react';
import { FolderOpen, Loader2 } from 'lucide-react';
import { Button } from '../UI/Button';
import { getGooglePickerConfig, openGooglePicker, type GooglePickerSelection } from '../../utils/googlePicker';

export interface GoogleDrivePickerButtonProps {
  onSelect: (selection: GooglePickerSelection) => void;
  allowFiles?: boolean;
  disabled?: boolean;
  className?: string;
}

export function GoogleDrivePickerButton({
  onSelect,
  allowFiles = false,
  disabled = false,
  className = '',
}: GoogleDrivePickerButtonProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const configured = getGooglePickerConfig() != null;

  const handleClick = async () => {
    setError(null);
    setLoading(true);
    try {
      const selections = await openGooglePicker({ allowFiles, multiSelect: false });
      if (selections.length > 0) {
        onSelect(selections[0]);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Google Picker failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={className}>
      <Button
        type="button"
        variant="outline"
        onClick={handleClick}
        disabled={disabled || loading || !configured}
        className="flex items-center gap-2"
      >
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <FolderOpen className="h-4 w-4" />}
        {loading ? 'Opening Drive...' : 'Browse Google Drive'}
      </Button>
      {!configured && (
        <p className="mt-1 text-xs text-amber-700 dark:text-amber-300">
          Picker requires VITE_GOOGLE_CLIENT_ID and VITE_GOOGLE_API_KEY. You can still enter folder IDs manually below.
        </p>
      )}
      {error && <p className="mt-1 text-xs text-red-600 dark:text-red-400">{error}</p>}
    </div>
  );
}
